/**
 * Survive a deploy that lands while the tab is open.
 *
 * The built app is a graph of content-hashed chunks. A deploy replaces every
 * one of them, so a tab that booted on `index-Blb4qOCK.js` is still asking for
 * the chunk names that build knew about — and the server no longer serves them.
 * The first lazy route the user visits after the deploy therefore dies:
 *
 *     loadable-components: failed to asynchronously load component
 *     TypeError: Failed to fetch dynamically imported module:
 *       https://…/assets/Calculators-BgR5sdr4.js
 *
 * `@loadable/component` has no retry, so the failure reaches the error boundary
 * and takes down the whole application — a full-page crash screen for what is,
 * underneath, a cache-busting problem that a reload fixes instantly. This is
 * very likely what was recorded as an unexplained "the app goes blank and won't
 * boot" incident.
 *
 * Vite raises `vite:preloadError` for precisely this case, so one listener
 * covers every dynamic import in the app without touching the ~80 `loadable`
 * call sites.
 *
 * The reload happens once. A chunk that is genuinely missing — a bad build, a
 * half-finished upload, an offline user — would otherwise reload forever, so
 * the attempt is recorded in `sessionStorage` and any second failure is allowed
 * through to the error boundary. `sessionStorage` is the right scope: it is
 * per-tab, and it is the tab that holds the stale module graph.
 */

const RELOAD_ATTEMPTED_KEY = 'curbside:stale-chunk-reloaded'

/** `sessionStorage` throws in some privacy modes; never break boot over it. */
const safeSession = {
  get(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      return null
    }
  },
  set(key: string, value: string): void {
    try {
      window.sessionStorage.setItem(key, value)
    } catch {
      /* no-op */
    }
  },
  remove(key: string): void {
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      /* no-op */
    }
  },
}

/**
 * Clear the guard once the app has booted successfully.
 *
 * Without this the flag survives for the life of the tab, so a *second* deploy
 * during a long session would not get its own reload.
 */
export const clearStaleChunkGuard = (): void => safeSession.remove(RELOAD_ATTEMPTED_KEY)

export const installStaleChunkReload = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    if (!navigator.onLine || safeSession.get(RELOAD_ATTEMPTED_KEY)) return

    // Stop Vite rethrowing, so the boundary does not paint a crash screen in the
    // moment before the reload takes over.
    event.preventDefault()
    safeSession.set(RELOAD_ATTEMPTED_KEY, '1')
    window.location.reload()
  })
}

export default installStaleChunkReload
